/**
 * Tour System Types
 * Defines the structure for onboarding tours
 */

export type TourStatus = 'not_started' | 'in_progress' | 'completed' | 'skipped';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'auto';

export type UserType = 'agent' | 'admin' | 'agency_owner' | 'trial' | 'viewer';

// Lowercase to match database constraint and actual values used throughout the app
// Also includes 'agency_owner' which is used in some backend responses
export type AgencyRole = 'owner' | 'admin' | 'agent' | 'viewer' | 'agency_owner';

export type AccountType = 'trial' | 'basic' | 'full_fledged' | 'enterprise';

/**
 * Context passed to conditional step functions
 */
export interface TourContext {
  userType: UserType;
  accountType: AccountType;
  agencyRole?: AgencyRole;
  hasAgency: boolean;
  isTrialUser: boolean;
  isTrialActive: boolean;
  isSystemAdmin: boolean;
}

/**
 * Individual step within a tour
 */
export interface TourStep {
  /** Unique identifier for this step */
  id: string;
  /** CSS selector to target the element (e.g., '[data-tour="metrics"]') */
  targetSelector: string;
  /** Title displayed in the tooltip */
  title: string;
  /** Description/body text for the step */
  description: string;
  /** Position of the tooltip relative to the target */
  placement: TooltipPlacement;
  /** Extra padding around the spotlight (default: 8) */
  spotlightPadding?: number;
  /** Conditionally skip this step based on user context */
  skipIf?: (context: TourContext) => boolean;
  /** Optional action to perform when this step is shown */
  onShow?: () => void;
  /** Optional action to perform before moving to next step */
  onNext?: () => void;
}

/**
 * Complete tour definition
 */
export interface TourDefinition {
  /** Unique identifier for the tour */
  id: string;
  /** Human-readable name */
  name: string;
  /** Route path where this tour runs (e.g., '/dashboard') */
  page: string;
  /** Version number - increment to re-show tour to users who completed it */
  version: number;
  /** Array of steps in order */
  steps: TourStep[];
  /** Optional: Only show to specific user types */
  allowedUserTypes?: UserType[];
  /** Optional: Only show to specific agency roles */
  allowedAgencyRoles?: AgencyRole[];
  /** Optional: Only show to users with an agency */
  requiresAgency?: boolean;
  /** Optional: Only show to system admins */
  requiresSystemAdmin?: boolean;
  /** Optional: Only show to specific account types */
  allowedAccountTypes?: AccountType[];
  /** Optional: Next tour ID to navigate to after completion (for tour chaining) */
  nextTourId?: string;
}

/**
 * Tour progress stored in database
 */
export interface TourProgress {
  id: string;
  userId: string;
  tourId: string;
  version: number;
  currentStep: number;
  status: TourStatus;
  startedAt?: string;
  completedAt?: string;
  skippedAt?: string;
  lastViewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Tour state managed by TourProvider
 */
export interface TourState {
  /** Currently active tour, if any */
  activeTour: TourDefinition | null;
  /** Current step index (0-based) */
  currentStepIndex: number;
  /** Whether tour is currently visible */
  isVisible: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Progress data from server */
  progress: Record<string, TourProgress>;
}

/**
 * Actions available in tour context
 */
export interface TourActions {
  /** Start a specific tour */
  startTour: (tourId: string) => Promise<void>;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  previousStep: () => void;
  /** Skip/dismiss the tour */
  skipTour: () => Promise<void>;
  /** Complete the tour */
  completeTour: () => Promise<void>;
  /** Reset tour for replay */
  resetTour: (tourId: string) => Promise<void>;
  /** Reset all tours for replay */
  resetAllTours: () => Promise<void>;
  /** Check if a tour should be shown */
  shouldShowTour: (tourId: string) => boolean;
  /** Get progress for a specific tour */
  getProgress: (tourId: string) => TourProgress | undefined;
  /** Refresh progress from server */
  refreshProgress: () => Promise<void>;
}

/**
 * Complete tour context type
 */
export interface TourContextType extends TourState, TourActions {}
